import { useOutletContext } from 'react-router-dom';
import { scoreboardWeek, weekStakes, type AwardRule } from '@ff/core';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { money, points } from '../lib/format.ts';
import { ManagerLink } from '../components/ManagerLink.tsx';

/**
 * The Survivor pool: who is still standing, and the graveyard of everyone the
 * pool has taken, a week at a time.
 *
 * Nothing is decided here. Who went out comes from the money board's own
 * survivor state, and this week's bubble from the same `weekStakes` the
 * scoreboard uses, so this page can't name a different victim than Home does.
 */
export function SurvivorAward({ rule }: { rule: AwardRule }) {
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const { data, money: board } = stored.bundle;
  const { alive, eliminations, winnerId, finalWeek } = board.survivor;

  // A week in progress that the pool plays: show it live, bubble and all.
  const openWeek = scoreboardWeek(data);
  const liveWeek =
    openWeek !== null && data.liveWeeks.includes(openWeek) && openWeek <= finalWeek && !winnerId
      ? openWeek
      : null;
  const bubble = new Set(
    liveWeek === null
      ? []
      : weekStakes(data, board, liveWeek)
          .filter((s) => s.kind === 'survivor' && !s.settled)
          .flatMap((s) => s.ownerIds),
  );

  // Between weeks the most useful number is how each survivor did last time;
  // during one, it's what they have on the board right now.
  const lastCalled = data.completedWeeks.length ? Math.max(...data.completedWeeks) : null;
  const shownWeek = liveWeek ?? lastCalled;
  const scoreIn = (ownerId: string) =>
    shownWeek === null
      ? null
      : (data.results.find((r) => r.ownerId === ownerId && r.week === shownWeek)?.score ?? null);
  const standing = alive
    .map((ownerId) => ({ ownerId, score: scoreIn(ownerId) }))
    // Lowest first, the manager in the most trouble at the top; then by name so
    // a pre-season pool isn't in arbitrary order.
    .sort(
      (a, b) =>
        (a.score ?? Infinity) - (b.score ?? Infinity) ||
        nameOf(a.ownerId).localeCompare(nameOf(b.ownerId)),
    );
  const graveyard = [...eliminations].sort(
    (a, b) => b.week - a.week || nameOf(a.ownerId).localeCompare(nameOf(b.ownerId)),
  );

  return (
    <div className="space-y-8">
      <div>
        <p className="eyebrow">
          {data.season} · last manager standing · {money(rule.amount)}
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          {rule.label}
        </h1>
        <p className="mt-2 max-w-lg text-sm text-chalk-dim">
          {rule.description} The pool is settled after Week {finalWeek}.
        </p>
      </div>

      {winnerId && (
        <div className="card flex items-center gap-4 p-4 ring-1 ring-cash/50">
          <span className="text-2xl" aria-hidden>
            🏆
          </span>
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-cash">Last manager standing</p>
            <p className="truncate font-display text-xl font-bold">
              <ManagerLink ownerId={winnerId}>{nameOf(winnerId)}</ManagerLink>
            </p>
          </div>
          <span className="font-mono text-2xl font-bold tabular text-cash">{money(rule.amount)}</span>
        </div>
      )}

      {!winnerId && (
        <section className="space-y-3">
          <div>
            <h2 className="eyebrow">Still Standing · {alive.length}</h2>
            <p className="mt-1 text-xs text-chalk-dim">
              {liveWeek !== null
                ? `Week ${liveWeek} is live. The lowest score among these when it's called goes out.`
                : shownWeek !== null
                  ? `Sorted by Week ${shownWeek}, lowest first.`
                  : 'No games yet. Everyone is alive until somebody posts the low score.'}
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {standing.map(({ ownerId, score }) => {
              const onBubble = bubble.has(ownerId);
              return (
                <div
                  key={ownerId}
                  className={`card flex items-center gap-3 p-3 ${onBubble ? 'ring-1 ring-blood/50' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <span className="truncate font-display font-bold">
                      <ManagerLink ownerId={ownerId}>{nameOf(ownerId)}</ManagerLink>
                    </span>
                    {onBubble && (
                      <p className="flex items-center gap-1.5 text-[0.7rem] text-blood">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blood" />
                        On the bubble
                      </p>
                    )}
                  </div>
                  {score !== null && (
                    <span
                      className={`font-mono text-lg font-bold tabular ${onBubble ? 'text-blood' : 'text-chalk'}`}
                    >
                      {points(score)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="eyebrow">The Graveyard · {eliminations.length}</h2>
        {graveyard.length === 0 ? (
          <p className="font-mono text-xs text-chalk-dim">Nobody's dead yet.</p>
        ) : (
          <div className="card divide-y divide-felt-line/60">
            {graveyard.map((e) => (
              <div key={`${e.week}-${e.ownerId}`} className="flex items-center gap-4 px-4 py-3 opacity-80">
                <div className="w-12 font-mono text-xs text-chalk-dim tabular">Wk {e.week}</div>
                <div className="min-w-0 flex-1">
                  <span className="truncate font-display font-bold line-through decoration-blood/70">
                    <ManagerLink ownerId={e.ownerId}>{nameOf(e.ownerId)}</ManagerLink>
                  </span>
                  <p className="text-[0.7rem] text-chalk-dim">
                    {e.remaining === 1 ? '1 left after this' : `${e.remaining} left after this`}
                  </p>
                </div>
                <span className="font-mono text-lg tabular text-blood">{points(e.score)}</span>
                <span aria-label="eliminated">☠️</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
